import { getIsMobile } from '@bridebook/web/app-basic/lib/app/selectors';
import { useSelector } from '@bridebook/web/app-basic/lib/utils';
import { memo, useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@bridebook/aura';
import { usePrevious } from '@bridebook/ui/src/utils/hooks';

const MIN_HEIGHT = 150;
const PREDICTION_DATA_NAME = 'suggestion-block';
const PREDICTION_SELECTED_DATA_NAME = 'suggestion-block-highlighted';
const OFFSET_BORDER = 2;
const OFFSET = 15;

interface AutocompleteResultsProps<D, A> {
  input: string;
  // Reference to parent element used to add key up event listener.
  parentRef: HTMLDivElement | null;
  items: D[];
  additional: A;
  onClick(values: D, additional?: A): void;
  onEscape?(e: KeyboardEvent): void;
  children(item: D, onClick: (item: D) => void): JSX.Element;
  onPositionChange?(position: 'top' | 'bottom'): void;
}
const AutocompleteResultsComponent = <D, A>({
  onClick,
  parentRef,
  input,
  onEscape,
  items,
  additional,
  children,
  onPositionChange,
}: AutocompleteResultsProps<D, A>) => {
  const isMobile = useSelector(getIsMobile);
  const [wrapperRef, setWrapperRef] = useState<HTMLDivElement>();
  const [additionalStyle, setAdditionalStyle] = useState({});
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [position, setPosition] = useState<'top' | 'bottom'>('top');
  const prevInput = usePrevious(input);

  const setWrapperRefCallback = useCallback((node: HTMLDivElement) => {
    setWrapperRef(node);
  }, []);

  const onClickCallback = useCallback(
    (prediction: D) => {
      setHighlightIndex(0);
      onClick(prediction, additional);
    },
    [additional, onClick],
  );

  const calculateSuggestionsHeight = useCallback(() => {
    const viewportHeight = window.visualViewport?.height;
    const elementRect = wrapperRef?.getBoundingClientRect();
    const parentRect = parentRef?.getBoundingClientRect() || ({} as DOMRect);

    if (elementRect && viewportHeight && wrapperRef) {
      const diff = viewportHeight - parentRect.bottom;
      const shouldDisplayOnTop = diff < MIN_HEIGHT;
      const scrollTop = document.documentElement.scrollTop;
      // set max available for space between parent top position and body height
      setAdditionalStyle(
        shouldDisplayOnTop
          ? {
              bottom: parentRect.height + diff - OFFSET_BORDER - scrollTop + 'px',
              left: parentRect.left + 'px',
              width: parentRect.width + 'px',
              maxHeight: `calc(  ${
                viewportHeight - parentRect.height - diff - OFFSET
              }px - env(safe-area-inset-top))`,
            }
          : {
              top: parentRect.bottom - OFFSET_BORDER + scrollTop + 'px',
              left: parentRect.left + 'px',
              width: parentRect.width + 'px',
              maxHeight: `calc( ${
                viewportHeight - parentRect.bottom - OFFSET
              }px - env(safe-area-inset-bottom))`,
            },
      );
      setPosition(shouldDisplayOnTop ? 'top' : 'bottom');
      onPositionChange?.(shouldDisplayOnTop ? 'top' : 'bottom');
    }
  }, [onPositionChange, parentRef, wrapperRef]);

  useLayoutEffect(() => {
    if (isMobile) {
      window.visualViewport?.addEventListener('resize', calculateSuggestionsHeight);
      return () => window.visualViewport?.removeEventListener('resize', calculateSuggestionsHeight);
    }
  }, [calculateSuggestionsHeight, isMobile]);

  useLayoutEffect(() => {
    calculateSuggestionsHeight();
  }, [calculateSuggestionsHeight]);

  // add key up event listener to parent element
  useEffect(() => {
    if (!parentRef) return;

    const keyUpListener = (e: KeyboardEvent) => {
      // find currently highlighted prediction
      const selectedEl: HTMLDivElement | null | undefined = wrapperRef?.querySelector(
        `[data-name="${PREDICTION_SELECTED_DATA_NAME}"]`,
      );
      if (!wrapperRef || !selectedEl) return;

      const scrollPos = selectedEl.offsetTop;
      switch (e.key) {
        case 'ArrowDown':
          // highlight next prediction
          if (highlightIndex < items.length - 1) {
            // scroll wrapper if required
            wrapperRef.scrollTop = scrollPos;
            setHighlightIndex(highlightIndex + 1);
          }
          break;
        case 'ArrowUp':
          // highlight previous prediction
          if (highlightIndex > 0) {
            // scroll wrapper if required
            wrapperRef.scrollTop = scrollPos - selectedEl?.offsetHeight;
            setHighlightIndex(highlightIndex - 1);
          }
          break;
        case 'Enter':
          // for Enter key behave like its clicked
          onClickCallback(items[highlightIndex]);
          break;
        default:
          // for Escape key call onEscape function from props
          e.key === 'Escape' && onEscape?.(e);

          // if previous input differs from current we need to reset scroll and index
          if (prevInput !== input) {
            wrapperRef.scrollTop = 0;
            setHighlightIndex(0);
          }
          return;
      }
    };

    parentRef.addEventListener('keyup', keyUpListener);
    return () => {
      parentRef.removeEventListener('keyup', keyUpListener);
    };
  }, [
    highlightIndex,
    input,
    onClickCallback,
    onEscape,
    parentRef,
    items,
    items.length,
    prevInput,
    wrapperRef,
  ]);

  const hasAnyPredictions = items.length;

  // show list only if there are any predictions
  return hasAnyPredictions
    ? createPortal(
        <div
          ref={setWrapperRefCallback}
          className={cn(
            'bsdrop',
            !isMobile && 'bsdrop-desktop',
            !isMobile && (position === 'bottom' ? 'bsdrop-b' : 'bsdrop-t'),
          )}
          style={additionalStyle}>
          {items.map((item, i) => {
            const isCurrentlyHighlighted = i === highlightIndex;
            const dataName = isCurrentlyHighlighted
              ? PREDICTION_SELECTED_DATA_NAME
              : PREDICTION_DATA_NAME;

            return (
              <div
                data-name={dataName}
                key={i}
                className={cn('bsitem', isCurrentlyHighlighted && 'bsitem-active')}>
                {children(item, onClickCallback)}
              </div>
            );
          })}
        </div>,
        document.body,
      )
    : null;
};

export const AutocompleteResults = memo(
  AutocompleteResultsComponent,
) as typeof AutocompleteResultsComponent;
